import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import styled from '@emotion/styled';
import { DataGridFlexSales, useDataGridFlexSales, useDataGridSync } from '../../DataGridFlexSales';

import {
  reportCausesFilterCauseSelector,
  reportMissingClosingCollectionsCausesSelector,
} from '../../../state';
import handlerFactory, { CollectionsMissingsDataGridContext } from './handlerFactory';
import {
  ValidationErrorCause,
  CollectionsDifferencesCause,
  MissingCauses,
  Translation,
  RemoteResource,
  MissingClosingCollectionsCauses,
} from '../../../types';
import { ValidationErrorsList } from '../ValidationErrorsList';

const Container = styled(Grid, {
  shouldForwardProp: prop => prop !== 'isDifferenceError',
})<{ isDifferenceError: boolean }>(({ isDifferenceError }) => ({
  width: '100%',
  ...(isDifferenceError
    ? {
        height: '100%',
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        '& [data-tour="report-results"]': {
          height: '100%',
          minHeight: 0,
        },
      }
    : {
        height: 180,
      }),
}));

interface DataGridConfigProps {
  t: Translation;
  resource: RemoteResource<MissingClosingCollectionsCauses | undefined>;
}

const DataGridConfig = ({ t, resource }: DataGridConfigProps) => {
  const { handler } = useDataGridFlexSales<
    CollectionsDifferencesCause | ValidationErrorCause,
    CollectionsMissingsDataGridContext
  >();

  const { loading } = resource;
  const data = resource.data?.details || [];

  const gridContext = useMemo(
    () => ({
      t,
    }),
    [t]
  );

  useDataGridSync({
    handler,
    data,
    context: gridContext,
    loading,
  });

  return null;
};

const CollectionErrors = () => {
  const { t } = useTranslation();
  const collectionsResource = useSelector(reportMissingClosingCollectionsCausesSelector);
  const cause = useSelector(reportCausesFilterCauseSelector);
  const isDifferenceError = cause === MissingCauses.DIFFERENCE_BETWEEN_CLOSING_AND_DETAIL;

  return (
    <Container isDifferenceError={isDifferenceError}>
      {isDifferenceError ? (
        <DataGridFlexSales handlerFactory={handlerFactory} hideToolbar>
          <DataGridConfig t={t} resource={collectionsResource} />
        </DataGridFlexSales>
      ) : (
        <ValidationErrorsList
          header={t('missing-closing-causes.columns.collections-details')}
          resource={collectionsResource}
        />
      )}
    </Container>
  );
};

export default CollectionErrors;
